import axios, { type AxiosProgressEvent } from 'axios';
import {
  User,
  Site,
  LoginResponse,
  MyTenantsResponse,
  StreamUsage,
  Tenant,
  TenantHealth,
  TenantMembership,
  TenantPlan,
  WidgetAlarm,
  OnboardingProgress,
  SupportFeedback,
  SupportResolutionTag,
  SupportTicket,
  SupportTicketCategory,
  SupportTicketPriority,
  SupportTicketResolution,
  SupportTicketStatus,
  SupportTicketThread,
} from '../types';

function getConfiguredApiBase(): string | undefined {
  const configured = process.env.REACT_APP_API_URL;
  if (configured !== undefined) {
    return configured.trim().replace(/\/+$/, '');
  }
  return undefined;
}

// Use REACT_APP_API_URL when provided. Production sets it to '/', which must
// stay as an empty same-origin base so Axios requests resolve to /api.
export function getApiBaseUrl(): string {
  const configured = getConfiguredApiBase();
  if (configured !== undefined) {
    return configured;
  }
  if (typeof window === 'undefined') return '';
  return `${window.location.protocol}//${window.location.hostname}:4000`;
}

export function getAssetUrl(assetPath?: string | null): string | null {
  if (!assetPath) return null;
  if (assetPath.startsWith('data:') || assetPath.startsWith('blob:')) {
    return assetPath;
  }
  if (/^https?:\/\//i.test(assetPath)) {
    if (typeof window === 'undefined') return assetPath;
    try {
      const assetUrl = new URL(assetPath);
      if (assetUrl.hostname === window.location.hostname && assetUrl.pathname.startsWith('/uploads/')) {
        return `${assetUrl.pathname}${assetUrl.search}${assetUrl.hash}`;
      }
    } catch {
      return assetPath;
    }
    return assetPath;
  }
  const normalizedPath = `/${assetPath.replace(/^\/+/, '')}`;
  if (typeof window === 'undefined') return normalizedPath;

  const configured = getConfiguredApiBase();
  if (!configured) return normalizedPath;

  try {
    const base = new URL(configured, window.location.origin);
    const sameBrowserHost = base.hostname === window.location.hostname;
    if (sameBrowserHost || base.origin === window.location.origin) {
      return normalizedPath;
    }
    return `${base.origin}${normalizedPath}`;
  } catch {
    return normalizedPath;
  }
}

const API_BASE = typeof window !== 'undefined'
  ? getApiBaseUrl()
  : '';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
});

export function getWebSocketUrl(path = '/ws'): string {
  if (typeof window === 'undefined') return path;

  const configured = getConfiguredApiBase();
  const base = configured === undefined
    ? `${window.location.protocol}//${window.location.hostname}:4000`
    : (configured || window.location.origin);
  const resolvedBase = new URL(base, window.location.origin);
  const url = new URL(path, resolvedBase.origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401 means the session itself is missing/expired. 403 means the user is
    // authenticated but not allowed to perform that action, so keep them logged in.
    if (error.response?.status === 401) {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      window.dispatchEvent(new Event('auth:logout'));
    }
    return Promise.reject(error);
  }
);

export default api;

export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await api.post('/auth/login', { email, password });
  return res.data;
}

export async function selectTenant(pendingToken: string, tenantId: string) {
  // One-off Authorization header — the pending token must not be read from
  // localStorage by the default interceptor.
  const res = await api.post(
    '/auth/select-tenant',
    { tenant_id: tenantId },
    { headers: { Authorization: `Bearer ${pendingToken}` } }
  );
  return res.data;
}

export async function switchTenant(tenantId: string) {
  const res = await api.post('/auth/switch-tenant', { tenant_id: tenantId });
  return res.data;
}

export async function getMyTenants(): Promise<MyTenantsResponse> {
  const res = await api.get('/auth/my-tenants');
  return res.data;
}

export async function register(email: string, password: string, tenant_name: string) {
  const res = await api.post('/auth/register', { email, password, tenant_name });
  return res.data;
}

// --- Module 19A: Identity Lifecycle ---

export interface SignupPayload {
  email: string;
  password: string;
  full_name: string;
  org_name: string;
  phone?: string;
  industry?: string;
  country?: string;
  company_size?: string;
  description?: string;
}

export async function signup(data: SignupPayload) {
  const res = await api.post('/auth/signup', data);
  return res.data as { user: User; email_verification_required: boolean; verification_url?: string };
}

export async function verifyEmail(token: string): Promise<any> {
  const inflight = (verifyEmail as any).__inflight as Map<string, Promise<any>>;
  if (inflight.has(token)) return inflight.get(token)!;
  const promise = api.post('/auth/verify-email', { token }).then((r) => r.data).finally(() => inflight.delete(token));
  inflight.set(token, promise);
  return promise;
}
(verifyEmail as any).__inflight = new Map<string, Promise<any>>();

export async function resendVerification(email: string) {
  const res = await api.post('/auth/resend-verification', { email });
  return res.data as { message: string; verification_url?: string };
}

export async function forgotPassword(email: string) {
  const res = await api.post('/auth/forgot-password', { email });
  return res.data as { message: string; reset_url?: string };
}

export async function resetPassword(token: string, password: string) {
  const res = await api.post('/auth/reset-password', { token, password });
  return res.data;
}

// Superadmin cross-tenant user management
export interface SuperAdminUser {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  role: string;
  signup_source: string | null;
  email_verified_at: string | null;
  last_login_at: string | null;
  is_active: boolean;
  suspended_by_platform: boolean;
  created_at: string;
  memberships: {
    tenant_id: string;
    tenant_name: string;
    role: string;
    industry: string | null;
    country: string | null;
    company_size: string | null;
    description: string | null;
    plan: string | null;
    trial_ends_at: string | null;
  }[];
}

export async function getSuperAdminUsers(params: { search?: string; limit?: number; offset?: number } = {}) {
  const res = await api.get('/super-admin/users', { params });
  return res.data as SuperAdminUser[];
}

export async function suspendPlatformUser(id: string) {
  const res = await api.post(`/super-admin/users/${id}/suspend`);
  return res.data;
}

export async function unsuspendPlatformUser(id: string) {
  const res = await api.post(`/super-admin/users/${id}/unsuspend`);
  return res.data;
}

export async function resetPlatformUserPassword(id: string) {
  const res = await api.post(`/super-admin/users/${id}/reset-password`);
  return res.data as { message: string; email_sent: boolean; reset_url?: string };
}

export async function resendPlatformUserVerification(id: string) {
  const res = await api.post(`/super-admin/users/${id}/resend-verification`);
  return res.data as { message: string; email_sent: boolean; verification_url?: string };
}

export interface EmailStats {
  date: string;
  budget: number;
  sent: number;
  failed: number;
  skipped_quota: number;
  skipped_cooldown: number;
  skipped_smtp_unconfigured: number;
  remaining: number;
}

export async function getEmailStats() {
  const res = await api.get('/super-admin/email-stats');
  return res.data as EmailStats;
}

// Signup alert recipients (superadmin-configured)
export interface SignupAlertRecipient {
  id: string;
  email: string;
  label: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface SignupAlertRecipientInput {
  email: string;
  label?: string | null;
  enabled?: boolean;
}

export async function getSignupAlertRecipients() {
  const res = await api.get('/super-admin/signup-alert-recipients');
  return res.data as SignupAlertRecipient[];
}

export async function createSignupAlertRecipient(data: SignupAlertRecipientInput) {
  const res = await api.post('/super-admin/signup-alert-recipients', data);
  return res.data as SignupAlertRecipient;
}

export async function updateSignupAlertRecipient(id: string, data: Partial<SignupAlertRecipientInput>) {
  const res = await api.patch(`/super-admin/signup-alert-recipients/${id}`, data);
  return res.data as SignupAlertRecipient;
}

export async function deleteSignupAlertRecipient(id: string) {
  await api.delete(`/super-admin/signup-alert-recipients/${id}`);
}

export async function logout() {
  const res = await api.post('/auth/logout');
  return res.data;
}

export async function getCurrentUser() {
  const res = await api.get('/auth/me');
  return res.data.user as User;
}

export async function restoreImpersonation() {
  const res = await api.post('/auth/restore-impersonation');
  return res.data.user as User;
}

export async function getDevices() {
  const res = await api.get('/devices');
  return res.data;
}

export async function getStreamUsage(): Promise<StreamUsage> {
  const res = await api.get('/devices/stream-usage');
  return res.data;
}

export async function getUsers() {
  const res = await api.get('/users');
  return res.data;
}

export async function deleteUser(id: string) {
  const res = await api.delete(`/users/${id}`);
  return res.data;
}

export async function getInvites() {
  const res = await api.get('/invites');
  return res.data;
}

export async function createInvite(data: { email: string; role: string }) {
  const res = await api.post('/invites', data);
  return res.data;
}

export async function deleteInvite(id: string) {
  const res = await api.delete(`/invites/${id}`);
  return res.data;
}

export async function validateInvite(token: string) {
  const res = await api.get(`/invites/accept/${token}`);
  return res.data;
}

export async function acceptInvite(token: string, password?: string) {
  // Existing accounts join without a password (empty body).
  const res = await api.post(`/invites/accept/${token}`, password ? { password } : {});
  return res.data;
}

export async function getAnalyticsSummary(siteId?: string) {
  const res = await api.get('/analytics/summary', {
    params: siteId ? { site_id: siteId } : {},
  });
  return res.data;
}

export async function getExportPreview(deviceId: string, start: string, end: string) {
  const res = await api.get('/analytics/export/preview', {
    params: { device_id: deviceId, start, end },
  });
  return res.data;
}

export async function exportCsv(
  deviceId: string,
  start: string,
  end: string,
  metrics: string[]
) {
  const res = await api.get('/analytics/export/csv', {
    params: { device_id: deviceId, start, end, metrics: metrics.join(',') },
    responseType: 'blob',
  });

  const blob = new Blob([res.data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);

  const disposition = res.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match ? match[1] : 'export.csv';

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function getReadings(deviceId?: string, hours = 24) {
  const res = await api.get('/analytics/readings', {
    params: { device_id: deviceId, hours },
  });
  return res.data;
}

export async function getAggregates(metric: string, hours = 24, siteId?: string) {
  const res = await api.get('/analytics/aggregates', {
    params: siteId ? { metric, hours, site_id: siteId } : { metric, hours },
  });
  return res.data;
}

export async function getAlerts() {
  const res = await api.get('/alerts');
  return res.data;
}

export async function createAlert(data: any) {
  const res = await api.post('/alerts', data);
  return res.data;
}

export async function deleteAlert(id: string) {
  const res = await api.delete(`/alerts/${id}`);
  return res.data;
}

export async function toggleAlert(id: string, enabled: boolean) {
  const res = await api.patch(`/alerts/${id}`, { enabled });
  return res.data;
}

export async function updateAlert(id: string, data: any) {
  const res = await api.put(`/alerts/${id}`, data);
  return res.data;
}

export async function getAlertLogs() {
  const res = await api.get('/alerts/logs');
  return res.data;
}

export async function sendTestEmail(email: string) {
  const res = await api.post('/alerts/test-email', { email });
  return res.data;
}

export async function sendTestTelegram(chatId: string) {
  const res = await api.post('/alerts/test-telegram', { chat_id: chatId });
  return res.data;
}

export async function getFloorPlan() {
  const res = await api.get('/tenants/floor-plan');
  return res.data;
}

export async function getAreaFloorPlan(areaId: string) {
  const res = await api.get(`/areas/${areaId}/floor-plan`);
  return res.data;
}

export async function uploadAreaFloorPlan(areaId: string, svg: string) {
  const res = await api.post(`/areas/${areaId}/floor-plan`, { svg });
  return res.data;
}

export async function uploadFloorPlan(svg: string) {
  const res = await api.post('/tenants/floor-plan', { svg });
  return res.data;
}

export async function updateDevicePosition(deviceId: string, pos_x: number, pos_y: number) {
  const res = await api.patch(`/devices/${deviceId}/position`, { pos_x, pos_y });
  return res.data;
}

export async function previewTopic(data: { name: string; location?: string }) {
  const res = await api.post('/devices/preview-topic', data);
  return res.data;
}

export async function createDevice(data: {
  name: string;
  location?: string;
  topic?: string;
  heartbeat_timeout_minutes?: number;
  gateway_topic?: string;
  device_identifier?: string;
  parser_type?: string;
  parser_config?: any;
  mqtt_connection_id?: string;
  area_id?: string | null;
}) {
  const res = await api.post('/devices', data);
  return res.data;
}

export async function updateDevice(deviceId: string, data: any) {
  const res = await api.put(`/devices/${deviceId}`, data);
  return res.data;
}

// Sites & Areas
export async function getSites(): Promise<Site[]> {
  const res = await api.get('/sites');
  return res.data;
}

export async function createSite(data: { name: string; description?: string }) {
  const res = await api.post('/sites', data);
  return res.data;
}

export async function updateSite(id: string, data: { name: string; description?: string }) {
  const res = await api.put(`/sites/${id}`, data);
  return res.data;
}

export async function deleteSite(id: string) {
  const res = await api.delete(`/sites/${id}`);
  return res.data;
}

export async function createArea(data: { site_id: string; name: string; description?: string }) {
  const res = await api.post('/areas', data);
  return res.data;
}

export async function updateArea(
  id: string,
  data: { name: string; description?: string; site_id?: string }
) {
  const res = await api.put(`/areas/${id}`, data);
  return res.data;
}

export async function deleteArea(id: string) {
  const res = await api.delete(`/areas/${id}`);
  return res.data;
}

export async function regenerateApiKey(deviceId: string) {
  const res = await api.post(`/devices/${deviceId}/regenerate-key`);
  return res.data;
}

export async function deleteDevice(deviceId: string) {
  const res = await api.delete(`/devices/${deviceId}`);
  return res.data;
}

export async function clearDeviceReadings(deviceId: string) {
  const res = await api.delete(`/devices/${deviceId}/readings`);
  return res.data;
}

export async function getReports() {
  const res = await api.get('/reports');
  return res.data;
}

export async function generateReport(data: {
  type: 'executive' | 'operational';
  title: string;
  description?: string;
  start: string;
  end: string;
  site_id?: string;
}) {
  const res = await api.post('/reports', data);
  return res.data;
}

export async function deleteReport(id: string) {
  const res = await api.delete(`/reports/${id}`);
  return res.data;
}

export async function sendReport(id: string, recipients: string[]) {
  const res = await api.post(`/reports/${id}/send`, { recipients });
  return res.data;
}

export async function downloadReport(id: string) {
  const res = await api.get(`/reports/${id}/download`, { responseType: 'blob' });
  return res.data as Blob;
}

export async function getReportSchedules() {
  const res = await api.get('/reports/schedules');
  return res.data;
}

export async function createReportSchedule(data: any) {
  const res = await api.post('/reports/schedules', data);
  return res.data;
}

export async function updateReportSchedule(id: string, data: any) {
  const res = await api.patch(`/reports/schedules/${id}`, data);
  return res.data;
}

export async function deleteReportSchedule(id: string) {
  const res = await api.delete(`/reports/schedules/${id}`);
  return res.data;
}

export async function runReportSchedule(id: string) {
  const res = await api.post(`/reports/schedules/${id}/run`);
  return res.data;
}

export async function getTenantSettings() {
  const res = await api.get('/tenants/settings');
  return res.data;
}

export interface MqttConfig {
  /** Public broker host; null = use the browser's current hostname. */
  host: string | null;
  /** Plain MQTT port. */
  port: number;
  /** MQTTS (TLS) port; null = TLS not available on this deployment. */
  tls_port: number | null;
}

export async function getMqttConfig(): Promise<MqttConfig> {
  const res = await api.get('/tenants/mqtt-config');
  return res.data;
}

export interface TenantSettingsPayload {
  timezone?: string;
  heartbeat_timeout_minutes?: number;
  platform_name?: string | null;
  platform_short_name?: string | null;
  platform_description?: string | null;
  branding_enabled?: boolean;
}

export async function getTenantPlan(): Promise<TenantPlan> {
  const res = await api.get('/tenants/plan');
  return res.data;
}

export async function updateTenantSettings(data: TenantSettingsPayload) {
  const res = await api.patch('/tenants/settings', data);
  return res.data;
}

export async function uploadTenantLogo(file: File, onProgress?: (progress: number) => void) {
  const formData = new FormData();
  formData.append('logo', file);
  const res = await api.post('/tenants/logo', formData, {
    onUploadProgress: (event: AxiosProgressEvent) => {
      if (!event.total || !onProgress) return;
      const progress = Math.round((event.loaded / event.total) * 100);
      onProgress(Math.min(95, Math.max(1, progress)));
    },
  });
  return res.data;
}

export async function deleteTenantLogo() {
  const res = await api.delete('/tenants/logo');
  return res.data;
}

export async function getMqttConnections() {
  const res = await api.get('/mqtt-connections');
  return res.data;
}

// Super Admin
export async function getSuperAdminTenants() {
  const res = await api.get('/super-admin/tenants');
  return res.data;
}

export async function getSuperAdminTenant(id: string) {
  const res = await api.get(`/super-admin/tenants/${id}`);
  return res.data;
}

export async function updateTenantPlan(id: string, plan: string): Promise<Partial<Tenant> & Pick<Tenant, 'id'>> {
  const res = await api.patch(`/super-admin/tenants/${id}/plan`, { plan });
  return res.data;
}

export async function updateTenantTrial(id: string, trial_ends_at: string | null): Promise<Partial<Tenant> & Pick<Tenant, 'id'>> {
  const res = await api.patch(`/super-admin/tenants/${id}/trial`, { trial_ends_at });
  return res.data;
}

export async function suspendTenant(id: string) {
  const res = await api.patch(`/super-admin/tenants/${id}/suspend`);
  return res.data;
}

export async function activateTenant(id: string) {
  const res = await api.patch(`/super-admin/tenants/${id}/activate`);
  return res.data;
}

export async function getSuperAdminMetrics() {
  const res = await api.get('/super-admin/metrics');
  return res.data;
}

export async function createSuperAdmin(data: { email: string; password: string }) {
  const res = await api.post('/super-admin/seed', data);
  return res.data;
}

export async function changeSuperAdminPassword(data: { current_password: string; new_password: string }) {
  const res = await api.patch('/super-admin/me/password', data);
  return res.data;
}

export async function createTenant(data: { tenant_name: string; admin_email: string; admin_password: string; plan?: string }) {
  const res = await api.post('/super-admin/tenants', data);
  return res.data;
}

export async function updateTenantAdmin(tenantId: string, data: { email?: string; password?: string }) {
  const res = await api.patch(`/super-admin/tenants/${tenantId}/admin`, data);
  return res.data;
}

export async function getSuperAdmins() {
  const res = await api.get('/super-admin/admins');
  return res.data;
}

export async function deactivateSuperAdmin(id: string) {
  const res = await api.patch(`/super-admin/admins/${id}/deactivate`);
  return res.data;
}

export async function activateSuperAdmin(id: string) {
  const res = await api.patch(`/super-admin/admins/${id}/activate`);
  return res.data;
}

export async function getTenantAdmins(tenantId: string) {
  const res = await api.get(`/super-admin/tenants/${tenantId}/admins`);
  return res.data;
}

export async function createTenantAdmin(tenantId: string, data: { email: string; password: string }) {
  const res = await api.post(`/super-admin/tenants/${tenantId}/admins`, data);
  return res.data;
}

export async function updateTenantAdminById(tenantId: string, userId: string, data: { email?: string; password?: string }) {
  const res = await api.patch(`/super-admin/tenants/${tenantId}/admins/${userId}`, data);
  return res.data;
}

export async function impersonateTenant(tenantId: string) {
  const res = await api.post(`/super-admin/impersonate/${tenantId}`);
  return res.data;
}

export interface SuperAdminAuditLogFilters {
  start?: string;
  end?: string;
  tenant_id?: string;
  event_type?: string;
  action?: string;
  severity?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export async function getSuperAdminAuditLogs(params: SuperAdminAuditLogFilters = {}) {
  const res = await api.get('/super-admin/audit-logs', { params });
  return res.data;
}

export async function getSuperAdminUsage(days = 30) {
  const res = await api.get('/super-admin/usage', { params: { days } });
  return res.data;
}

export async function getSuperAdminTenantHealth(): Promise<TenantHealth[]> {
  const res = await api.get('/super-admin/tenant-health');
  return res.data.tenants || [];
}

export async function getPlans() {
  const res = await api.get('/super-admin/plans');
  return res.data;
}

type PlanCreateInput = {
  name: string;
  slug: string;
  features: string[];
  max_devices?: number | null;
  max_users?: number | null;
  max_dashboards?: number | null;
  max_data_streams?: number | null;
};

type PlanUpdateInput = Partial<Omit<PlanCreateInput, 'slug'>> & {
  price_monthly?: number | null;
  currency?: string | null;
  is_public?: boolean;
};

export async function createPlan(data: PlanCreateInput) {
  const res = await api.post('/super-admin/plans', data);
  return res.data;
}

export async function updatePlan(id: string, data: PlanUpdateInput) {
  const res = await api.patch(`/super-admin/plans/${id}`, data);
  return res.data;
}

export async function deletePlan(id: string) {
  const res = await api.delete(`/super-admin/plans/${id}`);
  return res.data;
}

// Module 19B: public plan catalog and tenant trial/upgrade
export async function getPublicPlans(): Promise<import('../types').PlanPublic[]> {
  const res = await api.get('/plans/public');
  return res.data;
}

// Landing page demo/contact request (public, pre-login).
export async function submitDemoRequest(data: { email: string; company: string; message?: string }): Promise<void> {
  await api.post('/public/demo-request', data);
}

export async function getTenantTrialStatus(): Promise<import('../types').TenantTrialStatus> {
  const res = await api.get('/tenants/trial-status');
  return res.data;
}

export async function requestPlanUpgrade(target_plan_slug: string): Promise<import('../types').PlanUpgradeResponse> {
  const res = await api.post('/tenants/plan-upgrade-request', { target_plan_slug });
  return res.data;
}

export async function getOnboardingProgress(): Promise<OnboardingProgress> {
  const res = await api.get('/onboarding/progress');
  return res.data;
}

export async function updateOnboardingProgress(data: {
  dismissed?: boolean;
  overrides?: Record<string, boolean>;
}): Promise<{ ok: true }> {
  const res = await api.patch('/onboarding/progress', data);
  return res.data;
}

export async function getSupportTickets(): Promise<SupportTicket[]> {
  const res = await api.get('/support/tickets');
  return res.data.tickets || [];
}

export async function createSupportTicket(data: {
  subject: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  message: string;
  attachments?: string[];
}): Promise<{ ticket: SupportTicket }> {
  const res = await api.post('/support/tickets', data);
  return res.data;
}

export async function getSupportTicket(id: string): Promise<SupportTicketThread> {
  const res = await api.get(`/support/tickets/${id}`);
  return res.data;
}

export async function replySupportTicket(id: string, data: {
  body: string;
  attachments?: string[];
}): Promise<{ ok: true; status: SupportTicketStatus }> {
  const res = await api.post(`/support/tickets/${id}/reply`, data);
  return res.data;
}

export async function closeSupportTicket(id: string): Promise<{ ok: true; status: SupportTicketStatus }> {
  const res = await api.post(`/support/tickets/${id}/close`);
  return res.data;
}

export async function uploadSupportAttachment(file: File, onProgress?: (progress: number) => void): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post('/support/uploads', formData, {
    onUploadProgress: (event: AxiosProgressEvent) => {
      if (!event.total || !onProgress) return;
      const progress = Math.round((event.loaded / event.total) * 100);
      onProgress(Math.min(95, Math.max(1, progress)));
    },
  });
  if (onProgress) onProgress(100);
  return res.data;
}

export async function submitSupportFeedback(data: {
  rating: number;
  comment?: string;
  page?: string;
}): Promise<{ ok: true }> {
  const res = await api.post('/support/feedback', data);
  return res.data;
}

export async function getSuperAdminSupportTickets(params: {
  tenant_id?: string;
  status?: SupportTicketStatus | '';
  priority?: SupportTicketPriority | '';
  category?: SupportTicketCategory | '';
  q?: string;
} = {}): Promise<SupportTicket[]> {
  const res = await api.get('/super-admin/support/tickets', { params });
  return res.data.tickets || [];
}

export async function getSuperAdminSupportTicket(id: string): Promise<SupportTicketThread> {
  const res = await api.get(`/super-admin/support/tickets/${id}`);
  return res.data;
}

export async function replySuperAdminSupportTicket(id: string, data: {
  body: string;
  attachments?: string[];
}): Promise<{ ok: true; status: SupportTicketStatus }> {
  const res = await api.post(`/super-admin/support/tickets/${id}/reply`, data);
  return res.data;
}

export async function updateSuperAdminSupportTicket(id: string, data: {
  status?: SupportTicketStatus;
  priority?: SupportTicketPriority;
  assigned_to?: string | null;
}): Promise<{ ticket: SupportTicket }> {
  const res = await api.patch(`/super-admin/support/tickets/${id}`, data);
  return res.data;
}

export async function resolveSuperAdminSupportTicket(id: string, data: {
  customer_issue?: string | null;
  root_cause: string;
  fix_applied: string;
  prevention_note?: string | null;
  internal_notes?: string | null;
  customer_resolution: string;
  tags?: SupportResolutionTag[];
  kb_candidate?: boolean;
}): Promise<{ ticket: SupportTicket; resolution: SupportTicketResolution }> {
  const res = await api.post(`/super-admin/support/tickets/${id}/resolve`, data);
  return res.data;
}

export async function getSuperAdminSupportFeedback(): Promise<SupportFeedback[]> {
  const res = await api.get('/super-admin/support/feedback');
  return res.data.feedback || [];
}

export async function getPlatformSettings(): Promise<import('../types').PlatformSettings> {
  const res = await api.get('/super-admin/settings');
  return res.data;
}

export async function updatePlatformSetting(key: string, value: string): Promise<import('../types').PlatformSettings> {
  const res = await api.post('/super-admin/settings', { key, value });
  const updated = res.data as { key: string; value: string };
  return { [updated.key]: updated.value };
}

export async function createMqttConnection(data: {
  name: string;
  host: string;
  port: number;
  username?: string;
  password?: string;
  topics: string;
}) {
  const res = await api.post('/mqtt-connections', data);
  return res.data;
}

export async function deleteMqttConnection(id: string) {
  const res = await api.delete(`/mqtt-connections/${id}`);
  return res.data;
}

export async function toggleMqttConnection(id: string, enabled: boolean) {
  const res = await api.patch(`/mqtt-connections/${id}`, { enabled });
  return res.data;
}

export async function updateMqttConnection(id: string, data: {
  name: string;
  host: string;
  port: number;
  username?: string;
  password?: string;
  topics: string;
}) {
  const res = await api.put(`/mqtt-connections/${id}`, data);
  return res.data;
}

export async function getRecentExternalMessages() {
  const res = await api.get('/mqtt-connections/messages/recent');
  return res.data;
}

export async function clearExternalMessages() {
  const res = await api.post('/mqtt-connections/messages/clear');
  return res.data;
}

export async function publishMessage(connectionId: string, data: {
  topic: string;
  payload: string;
  format?: string;
  qos?: number;
  retain?: boolean;
}) {
  const res = await api.post(`/mqtt-connections/${connectionId}/publish`, data);
  return res.data;
}

export interface AuditLogFilters {
  start?: string;
  end?: string;
  user_id?: string;
  device_id?: string;
  event_type?: string;
  action?: string;
  severity?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export async function getAuditLogs(params: AuditLogFilters = {}) {
  const res = await api.get('/audit-logs', { params });
  return res.data;
}

export async function exportAuditLogsCsv(params: AuditLogFilters = {}) {
  const res = await api.get('/audit-logs/export/csv', { params, responseType: 'blob' });
  const blob = new Blob([res.data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-logs-${timestamp}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return res.data;
}

// Dashboard Builder (Module 12)
export async function getDashboards() {
  const res = await api.get('/dashboards');
  return res.data;
}

export async function getDashboard(id: string) {
  const res = await api.get(`/dashboards/${id}`);
  return res.data;
}

export async function createDashboard(data: { name: string; layout?: any; from_template_id?: string }) {
  const res = await api.post('/dashboards', data);
  return res.data;
}

export async function updateDashboard(id: string, data: { name?: string; layout?: any; is_default?: boolean }) {
  const res = await api.put(`/dashboards/${id}`, data);
  return res.data;
}

export async function deleteDashboard(id: string) {
  const res = await api.delete(`/dashboards/${id}`);
  return res.data;
}

export async function getDashboardTemplates() {
  const res = await api.get('/dashboard-templates');
  return res.data;
}

export async function createDashboardTemplate(data: {
  dashboard_id?: string;
  name?: string;
  industry?: string;
  description?: string;
  layout?: any;
}) {
  const res = await api.post('/dashboard-templates', data);
  return res.data;
}

export async function deleteDashboardTemplate(id: string) {
  const res = await api.delete(`/dashboard-templates/${id}`);
  return res.data;
}

export async function getWidgetData(data: {
  device_ids: string[];
  metric: string;
  range_hours?: number;
  bucket_size?: 'hour' | 'day' | 'week' | 'month';
  aggregation?: 'avg' | 'min' | 'max' | 'last' | 'runtime';
}) {
  const res = await api.post('/dashboards/widget-data', data);
  return res.data;
}

export async function getConsumptionData(data: {
  device_id: string;
  metric: string;
  range_hours?: number;
  bucket_size?: 'hour' | 'day' | 'week' | 'month';
  mode?: 'tank_drawdown' | 'cumulative_counter' | 'rate_total';
  refill_threshold?: number;
  counter_reset_threshold?: number;
  rate_time_unit?: 'hour' | 'minute' | 'second';
  tank_capacity?: number;
  min_valid?: number;
  max_valid?: number;
  max_gap_minutes?: number;
  max_daily_consumption?: number;
}): Promise<import('../types').ConsumptionDataResponse> {
  const res = await api.post('/dashboards/consumption-data', data);
  return res.data;
}

export async function getGpsMapData(data: {
  device_ids: string[];
  latitude_metric?: string;
  longitude_metric?: string;
  location_metric?: string;
  range_hours?: number;
}): Promise<{ points: { time: string; device_id: string; lat: number; lng: number }[] }> {
  const res = await api.post('/dashboards/gps-data', data);
  return res.data;
}

export async function getDeviceMetrics(deviceId: string) {
  const res = await api.get('/dashboards/metrics', { params: { device_id: deviceId } });
  return res.data;
}

export async function getWidgetAlarms(dashboardId: string): Promise<WidgetAlarm[]> {
  const res = await api.get('/dashboards/widget-alarms', { params: { dashboard_id: dashboardId } });
  return res.data;
}

export async function evaluateWidgetAlarms(dashboardId: string): Promise<{ alarms: WidgetAlarm[] }> {
  const res = await api.post('/dashboards/evaluate-alarms', { dashboard_id: dashboardId });
  return res.data;
}

export async function acknowledgeWidgetAlarm(alarmId: string): Promise<WidgetAlarm> {
  const res = await api.post(`/dashboards/widget-alarms/${alarmId}/acknowledge`);
  return res.data;
}

// --- Module 16: Enhanced Auth ---

export interface TwoFaSetupResponse {
  secret: string;
  otpauthUrl: string;
  backupCodes: string[];
}

export interface TwoFaVerifyResponse {
  enabled: boolean;
}

export type TwoFaVerifyLoginResponse =
  | { user: User; requires_tenant_selection?: false }
  | {
      requires_tenant_selection: true;
      pending_token: string;
      tenants: TenantMembership[];
      user?: undefined;
    };

export interface UserSession {
  id: string;
  ip_address: string | null;
  user_agent_hash: string;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  metadata?: Record<string, any>;
  is_current: boolean;
}

export async function setup2Fa(): Promise<TwoFaSetupResponse> {
  const res = await api.post('/auth/2fa/setup');
  return res.data;
}

export async function verify2FaSetup(code: string): Promise<TwoFaVerifyResponse> {
  const res = await api.post('/auth/2fa/verify-setup', { code });
  return res.data;
}

export async function disable2Fa(code: string): Promise<{ enabled: boolean }> {
  const res = await api.post('/auth/2fa/disable', { code });
  return res.data;
}

export async function regenerateBackupCodes(): Promise<{ backupCodes: string[] }> {
  const res = await api.post('/auth/2fa/backup-codes');
  return res.data;
}

export async function verify2FaLogin(token: string, code: string): Promise<TwoFaVerifyLoginResponse> {
  const res = await api.post('/auth/2fa/verify', { token, code });
  return res.data;
}

export async function getSessions(): Promise<{ sessions: UserSession[] }> {
  const res = await api.get('/auth/sessions');
  return res.data;
}

export async function revokeSession(sessionId: string): Promise<{ success: boolean }> {
  const res = await api.delete(`/auth/sessions/${sessionId}`);
  return res.data;
}

export async function revokeAllSessions(): Promise<{ revoked_count: number }> {
  const res = await api.delete('/auth/sessions');
  return res.data;
}

export async function exportAuditLogsJson(params: AuditLogFilters = {}) {
  const res = await api.get('/audit-logs/export/json', { params, responseType: 'blob' });
  const blob = new Blob([res.data], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-logs-${timestamp}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return res.data;
}
